/**
 * The one upload driver for a fan's photo: a `File` in, a live profile photo,
 * a photo waiting on a moderation result, or a retryable failure out.
 *
 * `/upload/complete` is synchronous and terminal: `postUploadCompleteHandler`
 * runs the resize, the variant writes, the moderation decision and the final
 * status write inline in the one request and returns the terminal status.
 * There is no asynchronous phase after it returns, so nothing here polls, and
 * the six-value get-status polling hook is deliberately not imported.
 *
 * ISSUE-887, publish first and review after: `/upload/complete` now answers
 * `"published"` for every upload, whatever the scan concluded, so the
 * ordinary path through this hook ends at `published` with a real photoUrl
 * and a fan sees their photo immediately. A flagged or unscanned image is
 * published too and queued for staff, who can take it down later; that
 * takedown flips the media row to `rejected`, which makes `photoUrl`
 * resolve to null again on the next read with no client change.
 *
 * `avatar/set` is called only when `/upload/complete` answers `"published"`.
 * `postAvatarSetHandler/index.ts:95` refuses any other status with
 * `VALIDATION_ERROR` / `assetFlaggedByModeration`, so calling it on a
 * `pendingReview` or `heldOnBudget` asset is a guaranteed error, not a race
 * worth retrying.
 *
 * Onboarding must never wait on a moderation result and this step must never
 * deadlock, so a pending or failed upload leaves `Continue` and `Skip` both
 * enabled; this hook has no notion of blocking the caller.
 */
import { FILE_TOO_LARGE_COPY } from "@sportsmet/errors";
import { useUploadComplete, useUploadInit } from "@sportsmet/media-sdk/private";
import { useAvatarSet } from "@sportsmet/profile-sdk/private";
import { useCallback, useRef, useState } from "react";

import { photoUploadRefusalMessage } from "../../errors/apiErrorBus";

export const MAX_AVATAR_UPLOAD_BYTES = 15_000_000;

export const AVATAR_UPLOAD_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
] as const;

export type typeAvatarUploadContentType =
  (typeof AVATAR_UPLOAD_CONTENT_TYPES)[number];

export type typeAvatarUploadState =
  | { kind: "idle" }
  | { kind: "uploading" }
  | { kind: "published"; photoUrl: string }
  | { kind: "pendingReview" }
  | { kind: "failed"; message: string | null };

export function useAvatarUpload(): {
  state: typeAvatarUploadState;
  uploadAvatar: (file: File) => void;
  reset: () => void;
} {
  const uploadInit = useUploadInit();
  const uploadComplete = useUploadComplete();
  const avatarSet = useAvatarSet();
  const [state, setState] = useState<typeAvatarUploadState>({ kind: "idle" });
  // The re-entrancy guard lives on a ref, not on `state`, so the check inside
  // `run` always sees the latest value rather than whatever `state` was when
  // the enclosing callback was created.
  const isUploadingRef = useRef(false);

  const uploadAvatar = useCallback(
    (file: File) => {
      // 1. Re-entrancy guard. One upload at a time.
      if (isUploadingRef.current) return;

      async function run(): Promise<void> {
        // 2. Client-side pre-checks, before any network call. These mirror
        // the server's own refusals (UNSUPPORTED_CONTENT_TYPE and
        // FILE_TOO_LARGE) so an oversized or wrong-type file costs the fan a
        // local check instead of a round trip. The server still refuses
        // independently; this is a courtesy gate, never the real one.
        if (
          !(AVATAR_UPLOAD_CONTENT_TYPES as readonly string[]).includes(
            file.type,
          )
        ) {
          setState({ kind: "failed", message: null });
          return;
        }
        // An empty file and an oversized one are two different refusals. Only
        // the oversized one is a size problem, so only it gets the size
        // sentence (ISSUE-839).
        if (file.size > MAX_AVATAR_UPLOAD_BYTES) {
          setState({ kind: "failed", message: FILE_TOO_LARGE_COPY });
          return;
        }
        if (file.size < 1) {
          setState({ kind: "failed", message: null });
          return;
        }

        // 3. Mint the request id once and reuse it for the rest of this call.
        isUploadingRef.current = true;
        setState({ kind: "uploading" });
        const requestId = crypto.randomUUID();

        // 4. /upload/init
        let init;
        try {
          init = await uploadInit.mutateAsync({
            purpose: "avatar",
            contentType: file.type as typeAvatarUploadContentType,
            byteSize: file.size,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (init.error != null || init.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(init),
          });
          return;
        }

        // 5. The presigned PUT. The browser does this itself with a bare
        // fetch, never through any SDK: the URL is presigned and points at
        // object storage, not at a sportsmet service, so it carries no
        // session header and no browser-managed session cookies. The whole
        // File is the body; no FormData, no multipart, no chunking.
        let putResponse: Response;
        try {
          putResponse = await fetch(init.data.uploadUrl, {
            method: init.data.uploadMethod,
            headers: { "content-type": init.data.uploadHeaders.contentType },
            body: file,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (!putResponse.ok) {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }

        // 6. /upload/complete. Its status has exactly three possible values.
        let complete;
        try {
          complete = await uploadComplete.mutateAsync({
            mediaId: init.data.mediaId,
            requestId,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (complete.error != null || complete.data == null) {
          isUploadingRef.current = false;
          setState({
            kind: "failed",
            message: photoUploadRefusalMessage(complete),
          });
          return;
        }
        // ISSUE-887 made /upload/complete publish every outcome, so these
        // two statuses no longer come back from a fresh upload. The branch
        // stays because a client is not the thing that gets to assume that:
        // an older media-api during a rolling deploy still answers with
        // them, and falling through to avatar/set on one would earn a
        // guaranteed VALIDATION_ERROR instead of a sentence a fan can read.
        if (
          complete.data.status === "pendingReview" ||
          complete.data.status === "heldOnBudget"
        ) {
          isUploadingRef.current = false;
          setState({ kind: "pendingReview" });
          return;
        }

        // 7. /avatar/set, only ever reached when /upload/complete said
        // published.
        let attached;
        try {
          attached = await avatarSet.mutateAsync({
            assetId: complete.data.mediaId,
          });
        } catch {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }
        if (attached.error != null || attached.data?.profile.photoUrl == null) {
          isUploadingRef.current = false;
          setState({ kind: "failed", message: null });
          return;
        }

        isUploadingRef.current = false;
        setState({
          kind: "published",
          photoUrl: attached.data.profile.photoUrl,
        });
      }

      void run();
    },
    [uploadInit, uploadComplete, avatarSet],
  );

  const reset = useCallback(() => {
    setState({ kind: "idle" });
  }, []);

  return { state, uploadAvatar, reset };
}
